import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { apiJson } from "../api/client";
import { setSelectedCompany as persistSelectedCompany } from "../utils/tenantContext";

export const TENANT_SCOPE_FRAMEWORK_VERSION =
  "wp-tenant-scope-framework-v1";

export const TENANT_SCOPE_RUNTIME_VERSION =
  "wp-r5-phase3-tenant-scope-runtime-v1";

const TenantScopeContext = createContext(null);

const STORAGE = {
  companyId: "wp_scope_company_id",
  deviceUuid: "wp_scope_device_uuid",
  deviceId: "wp_scope_device_id",
  platform: "wp_scope_platform",
  startDate: "wp_scope_start_date",
  endDate: "wp_scope_end_date",
  status: "wp_scope_status",
  severity: "wp_scope_severity",
};

function stored(key, fallback = "") {
  const value = localStorage.getItem(key);
  return value === null ? fallback : value;
}

export function normalizeScopePlatform(value) {
  const platform = String(value || "").trim().toLowerCase();

  if (
    platform === "darwin" ||
    platform === "mac" ||
    platform === "macos" ||
    platform === "osx" ||
    platform.startsWith("mac os")
  ) {
    return "macos";
  }

  if (platform.startsWith("win")) {
    return "windows";
  }

  if (platform.startsWith("linux")) {
    return "linux";
  }

  if (
    platform === "ios" ||
    platform === "ipados" ||
    platform.startsWith("iphone") ||
    platform.startsWith("ipad")
  ) {
    return "ios";
  }

  if (platform.startsWith("android")) {
    return "android";
  }

  return platform || "unknown";
}

function sessionSnapshot() {
  return {
    role: localStorage.getItem("role") || "",
    companyId: localStorage.getItem("company_id") || "",
    username: localStorage.getItem("username") || "",
    tokenPresent: Boolean(localStorage.getItem("access_token")),
  };
}

export function TenantScopeProvider({ children }) {
  const session = sessionSnapshot();
  const isSuperAdmin = session.role === "super_admin";
  const isTenantAdmin = ["company_admin", "client_admin"].includes(session.role);

  const [companies, setCompanies] = useState([]);
  const [devices, setDevices] = useState([]);
  const [companyId, setCompanyIdState] = useState(
    isSuperAdmin
      ? stored(STORAGE.companyId, session.companyId)
      : session.companyId
  );
  const [deviceUuid, setDeviceUuid] = useState(stored(STORAGE.deviceUuid, ""));
  const [platform, setPlatformState] = useState(
    stored(STORAGE.platform, "all")
  );
  const [startDate, setStartDate] = useState(stored(STORAGE.startDate, ""));
  const [endDate, setEndDate] = useState(stored(STORAGE.endDate, ""));
  const [status, setStatus] = useState(stored(STORAGE.status, "all"));
  const [severity, setSeverity] = useState(stored(STORAGE.severity, "all"));
  const [loadingScope, setLoadingScope] = useState(false);
  const [scopeError, setScopeError] = useState("");

  const effectiveCompanyId = isSuperAdmin ? companyId : session.companyId;

  const clearDeviceScope = useCallback(() => {
    setDevices([]);
    setDeviceUuid("");
    localStorage.removeItem(STORAGE.deviceUuid);
    localStorage.removeItem(STORAGE.deviceId);
  }, []);

  const setCompanyId = useCallback(
    (nextCompanyId) => {
      if (!isSuperAdmin) {
        setCompanyIdState(session.companyId);
        clearDeviceScope();
        return;
      }
      setCompanyIdState(nextCompanyId ? String(nextCompanyId) : "");
      clearDeviceScope();
    },
    [clearDeviceScope, isSuperAdmin, session.companyId]
  );

  const loadCompanies = useCallback(async () => {
    if (!session.tokenPresent) {
      setCompanies([]);
      return [];
    }

    if (!isSuperAdmin) {
      setCompanies([]);
      setCompanyIdState(session.companyId);
      return [];
    }

    const data = await apiJson("/auth/list-companies");
    const rows = Array.isArray(data?.companies) ? data.companies : [];
    setCompanies(rows);

    setCompanyIdState((current) => {
      if (
        current &&
        rows.some((company) => String(company.id) === String(current))
      ) {
        return String(current);
      }

      if (
        session.companyId &&
        rows.some(
          (company) => String(company.id) === String(session.companyId)
        )
      ) {
        return String(session.companyId);
      }

      return rows.length ? String(rows[0].id) : "";
    });

    return rows;
  }, [isSuperAdmin, session.companyId, session.tokenPresent]);

  const loadDevices = useCallback(async () => {
    const targetCompanyId = isSuperAdmin ? companyId : session.companyId;

    if (!session.tokenPresent || !targetCompanyId) {
      clearDeviceScope();
      return [];
    }

    const rows = await apiJson(
      `/devices/company/${encodeURIComponent(targetCompanyId)}`
    );

    const safeRows = Array.isArray(rows) ? rows : [];
    const tenantRows = safeRows.filter(
      (device) =>
        String(device.company_id || "") === String(targetCompanyId)
    );

    setDevices(tenantRows);

    setDeviceUuid((current) =>
      current &&
      tenantRows.some(
        (device) => String(device.device_uuid) === String(current)
      )
        ? current
        : ""
    );

    return tenantRows;
  }, [
    clearDeviceScope,
    companyId,
    isSuperAdmin,
    session.companyId,
    session.tokenPresent,
  ]);

  const refreshScope = useCallback(async () => {
    if (!session.tokenPresent) {
      setScopeError("");
      setCompanies([]);
      clearDeviceScope();
      return;
    }

    setLoadingScope(true);
    setScopeError("");

    try {
      if (isSuperAdmin) await loadCompanies();
      await loadDevices();
    } catch (error) {
      setScopeError(error?.message || "Unable to load dashboard scope.");
    } finally {
      setLoadingScope(false);
    }
  }, [
    clearDeviceScope,
    isSuperAdmin,
    loadCompanies,
    loadDevices,
    session.tokenPresent,
  ]);

  const setPlatform = useCallback((nextPlatform) => {
    const normalized =
      String(nextPlatform || "all").toLowerCase() === "all"
        ? "all"
        : normalizeScopePlatform(nextPlatform);

    setPlatformState(normalized);
  }, []);

  const resetFilters = useCallback(() => {
    setDeviceUuid("");
    setPlatformState("all");
    setStartDate("");
    setEndDate("");
    setStatus("all");
    setSeverity("all");
  }, []);

  const selectedCompany = useMemo(
    () =>
      companies.find(
        (company) => String(company.id) === String(effectiveCompanyId)
      ) || null,
    [companies, effectiveCompanyId]
  );

  const scopedDevices = useMemo(() => {
    if (platform === "all") {
      return devices;
    }

    return devices.filter(
      (device) =>
        normalizeScopePlatform(device.platform) === platform
    );
  }, [devices, platform]);

  const selectedDevice = useMemo(
    () =>
      scopedDevices.find(
        (device) => String(device.device_uuid) === String(deviceUuid)
      ) || null,
    [deviceUuid, scopedDevices]
  );

  useEffect(() => {
    if (
      deviceUuid &&
      !scopedDevices.some(
        (device) => String(device.device_uuid) === String(deviceUuid)
      )
    ) {
      setDeviceUuid("");
    }
  }, [deviceUuid, scopedDevices]);

  useEffect(() => {
    if (isSuperAdmin) {
      localStorage.setItem(
        STORAGE.companyId,
        effectiveCompanyId || ""
      );

      const currentCompany =
        companies.find(
          (company) =>
            String(company.id) ===
            String(effectiveCompanyId)
        ) || null;

      persistSelectedCompany(
        effectiveCompanyId || "",
        currentCompany?.company_name ||
          currentCompany?.name ||
          ""
      );
    } else {
      localStorage.removeItem(STORAGE.companyId);

      persistSelectedCompany(
        session.companyId || "",
        ""
      );
    }
  }, [
    companies,
    effectiveCompanyId,
    isSuperAdmin,
    session.companyId,
  ]);

  useEffect(() => {
    localStorage.setItem(STORAGE.platform, platform || "all");
  }, [platform]);

  useEffect(() => {
    localStorage.setItem(STORAGE.deviceUuid, deviceUuid || "");
    const deviceId = selectedDevice?.id ?? "";
    if (deviceId === "") {
      localStorage.removeItem(STORAGE.deviceId);
    } else {
      localStorage.setItem(STORAGE.deviceId, String(deviceId));
    }
  }, [deviceUuid, selectedDevice]);

  useEffect(() => {
    localStorage.setItem(STORAGE.startDate, startDate || "");
    localStorage.setItem(STORAGE.endDate, endDate || "");
    localStorage.setItem(STORAGE.status, status || "all");
    localStorage.setItem(STORAGE.severity, severity || "all");
  }, [endDate, severity, startDate, status]);

  useEffect(() => {
    if (session.tokenPresent && isSuperAdmin) {
      loadCompanies().catch(() => {});
    }
  }, [isSuperAdmin, loadCompanies, session.tokenPresent]);

  useEffect(() => {
    if (session.tokenPresent && effectiveCompanyId) {
      loadDevices().catch(() => {});
    }
  }, [effectiveCompanyId, loadDevices, session.tokenPresent]);

  const value = useMemo(
    () => ({
      frameworkVersion: TENANT_SCOPE_FRAMEWORK_VERSION,
      runtimeVersion: TENANT_SCOPE_RUNTIME_VERSION,
      role: session.role,
      username: session.username,
      sessionCompanyId: session.companyId,
      tokenPresent: session.tokenPresent,
      isSuperAdmin,
      isTenantAdmin,
      companies,
      devices,
      scopedDevices,
      companyId: effectiveCompanyId,
      deviceUuid,
      platform,
      startDate,
      endDate,
      status,
      severity,
      selectedCompany,
      selectedDevice,
      loadingScope,
      scopeError,
      setCompanyId,
      setDeviceUuid,
      setPlatform,
      setStartDate,
      setEndDate,
      setStatus,
      setSeverity,
      loadCompanies,
      loadDevices,
      refreshScope,
      resetFilters,
      clearDeviceScope,
    }),
    [
      clearDeviceScope,
      companies,
      deviceUuid,
      devices,
      scopedDevices,
      effectiveCompanyId,
      endDate,
      isSuperAdmin,
      isTenantAdmin,
      loadCompanies,
      loadDevices,
      loadingScope,
      refreshScope,
      resetFilters,
      scopeError,
      selectedCompany,
      selectedDevice,
      platform,
      session.companyId,
      session.role,
      session.tokenPresent,
      session.username,
      setCompanyId,
      setPlatform,
      severity,
      startDate,
      status,
    ]
  );

  return (
    <TenantScopeContext.Provider value={value}>
      {children}
    </TenantScopeContext.Provider>
  );
}

export function useTenantScope() {
  const context = useContext(TenantScopeContext);

  if (!context) {
    throw new Error(
      "useTenantScope must be used inside TenantScopeProvider."
    );
  }

  return context;
}
